// -------------------------
// React
// -------------------------
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";

import DateObject from "react-date-object";

// -------------------------
// Third-party libraries
// -------------------------
import { Card, Col, Row, Button, Offcanvas } from "react-bootstrap";
import {
  WarningCard,
  LoadingSpinner,
  InterferenceAlertCard,
  DateTimePickerEdit,
} from "../../components";
import {
  ClockHistory,
  ConeStriped,
  ExclamationDiamond,
  ExclamationOctagon,
  ExclamationSquare,
  ExclamationTriangle,
  Hash,
  Hourglass,
  Reception4,
  Soundwave,
  UpcScan,
} from "react-bootstrap-icons";

import RealtimeAreaGraph from "../graphs/RealtimeAreaGraph";
import RealtimeDotsGraph from "../graphs/RealtimeDotsGraph";
import PolarPlot from "../graphs/PolarPlot";
import DotsGraph from "../graphs/DotsGraph";
import AreaGraph from "../graphs/AreaGraph";

// -------------------------
// Utilities / helpers
// -------------------------
import { COLORS } from "utils/colors";
import { formatFrequency, formatDuration } from "utils/helpers/formatters";
import { CardComponent } from "./CardComponent";

import { useGnssApi, useWebSocket, useAuth } from "utils/hooks";
import RealtimePolarGraph from "../graphs/RealtimePolarGraph";
import { use } from "react";

// -------------------------
// Constants
// -------------------------
const AlertState = {
  FIRING: "FIRING",
  STOPPED: "STOPPED",
};

const severityConfig = {
  INFO: {
    color: "text-success",
    label: "Informational",
    Icon: ExclamationTriangle,
  },
  WARNING: {
    color: "text-warning",
    label: "Warning",
    Icon: ExclamationDiamond,
  },
  CRITICAL: {
    color: "text-danger",
    label: "Critical",
    Icon: ExclamationOctagon,
  },
};

const titleStyles = {
  INFO: "text-success",
  WARNING: "blink text-warning",
  CRITICAL: "blink text-danger",
};

// -------------------------
// Component
// -------------------------
/**
 * InterferenceAlertCard
 *
 * Displays detailed information about a single interference alert.
 *
 * Props:
 * @param {Object} alertDetails - The alert information.
 * @param {string} alertDetails.id - Unique identifier for the alert.
 * @param {string} alertDetails.state - Current state of the alert (FIRING/STOPPED).
 * @param {string} alertDetails.type - Type of the alert.
 * @param {string} alertDetails.eventType - Event type description.
 * @param {string} alertDetails.startTime - ISO string of when the alert started.
 * @param {string} alertDetails.severity - Severity level (INFO/WARNING/CRITICAL).
 * @param {number} alertDetails.duration - Duration in seconds if stopped.
 * @param {number} alertDetails.centralFreq - Central frequency in Hz.
 * @param {number} alertDetails.bandwidth - Bandwidth in Hz.
 */

const RealitimeAnalyticsComponent = ({ stationId }) => {
  const { selected_tenant_id } = useAuth();

  const [isConnected, subscribe, unsubscribe] = useWebSocket();
  const channelName = `${selected_tenant_id}-${stationId}-interferences`;

  const [newSamplePerformance, setNewSamplePerformance] = useState([]);
  const [newSampleDirection, setNewSampleDirection] = useState([]);

  useEffect(() => {
    if (isConnected === true) {
      // Subscribe to the desired WebSocket channel
      subscribe(channelName, handleGnssReceiveCallbackMessage);
    }
    // Cleanup subscription on component unmount
    return () => {
      unsubscribe(channelName);
    };
  }, [isConnected, subscribe, unsubscribe]);

  // const maxBandwidth = useMemo(() => {
  //   if (!alertDataset?.length) return 1;

  //   return alertDataset.reduce((max, d) => {
  //     const value = d.bandwidth / 1e6;
  //     return value > max ? value : max;
  //   }, 0);
  // }, [alertDataset]);

  // const [maxBandwidth, setMaxBandwidth] = useState(() => {
  //   if (!alertDataset?.length) return 1;

  //   return alertDataset.reduce((max, d) => {
  //     const value = d.bandwidth / 1e6;
  //     return value > max ? value : max;
  //   }, 0);
  // });

  const [maxBandwidth, setMaxBandwidth] = useState(0.02);
  const [nrSv, setNrSv]= useState([]);

  useEffect(() => {
    if (!newSamplePerformance?.length) return;

    const batchMax = newSamplePerformance.reduce((max, d) => {
      const value = d.interferenceBandwidth / 1e3;
      return value > max ? value : max;
    }, 0);

    setMaxBandwidth((prevMax) => (batchMax > prevMax ? batchMax : prevMax));
  }, [newSamplePerformance]);

  var handleGnssReceiveCallbackMessage = useCallback(
    (stompMessage) => {
      const payload = JSON.parse(stompMessage.body);

      const data = payload?.data;
      const details = data?.interferenceEvents;
      
      setNrSv((prev)=> [...prev, {interferenceFrequency: data?.nrSV} ]);

      if (!details?.interferenceDetails?.length) return;

      const sample = details.interferenceDetails.map((item) => ({
        ...item,
        nrSV: data?.nrSV,
      }));

      if (sample) {
        setNewSamplePerformance(sample);
      } else setNewSamplePerformance([]);
    },
    [stationId],
  );

  return (
    <>
      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={newSamplePerformance.map((sample) => ({
                interferenceFrequency: sample?.interferenceFrequency / 1e6,
              }))}
              defaultOptiopns={{
                title: "Interference central frequency over time",
                xLabelName: "Time",
                yLabelName: "Frequency(MHz)",
                yMinValue: 1000,
                yMaxValue: 1800,
              }}
              lookback={160}
              // initialDataset={alertDataset?.interferenceFrequency?.map(
              //   (sample) => ({
              //     timestamp: sample?.timestamp,
              //     interferenceFrequency: sample?.value / 10 ** 6,
              //   }),
              // )}
              freeze={false}
            ></RealtimeDotsGraph>
          </Card>
        </Col>

        <Col className="col-md-12 col-lg-12 col-xl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={
                newSamplePerformance
                  ?.filter((sample) => sample?.bearing != null)
                  ?.map((sample) => ({
                    interferenceFrequency: sample?.bearing,
                  })) ?? []
              }
              defaultOptiopns={{
                title: "Bearing over time XY plot",
                xLabelName: "Time",
                yLabelName: "Bearing (degs)",
                yMinValue: 0,
                yMaxValue: 360,
                yTicks: 6,
              }}
              lookback={160}
              // initialDataset={alertDataset?.bearing?.map((sample) => ({
              //   timestamp: sample.timestamp,
              //   interferenceFrequency: sample?.bearing,
              // }))}
              freeze={false}
            ></RealtimeDotsGraph>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0 d-flex flex-column">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={nrSv}
              defaultOptiopns={{
                title: "Nr. of satellites",
                xLabelName: "Time",
                yLabelName: "Count",
                yMinValue: 0,
                yMaxValue: 50,
              }}
              lookback={160}
              // initialDataset={alertDataset?.nrSV?.map((sample) => ({
              //   timestamp: sample?.timestamp,
              //   interferenceFrequency: sample?.value,
              // }))}
              freeze={false}
            ></RealtimeDotsGraph>
          </Card>
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeAreaGraph
              width={600}
              height={250}
              interferences={newSamplePerformance
                ?.filter((sample) => sample?.interferenceBandwidth != null)
                ?.map((sample) => ({
                  interferenceFrequency:
                    sample?.interferenceBandwidth / 10 ** 3,
                }))}
              defaultOptiopns={{
                title: "Interference bandwidth over time",
                xLabelName: "Time",
                yLabelName: "Bandwidth(kHz)",
                yMinValue: 0,
                yTicks: 6,
              }}
              lookback={160}
              // initialDataset={alertDataset?.bandwidth?.map((sample) => ({
              //   timestamp: sample.timestamp,
              //   interferenceFrequency: sample.value / 10 ** 3,
              // }))}
              freeze={false}
            ></RealtimeAreaGraph>
          </Card>
        </Col>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0 d-flex flex-column">
          <Card
            className="m-2 p-2 flex-fill justify-content-center align-items-center"
            style={{ background: "#141414" }}
          >
            <RealtimePolarGraph
              width={550}
              height={550}
              defaultOptiopns={{
                title: "Bearing realtime",
              }}
              azTarget={
                newSamplePerformance
                  .map((sample) => ({
                    bearing: sample?.bearing,
                    centralFreq: formatFrequency(sample?.interferenceFrequency),
                  }))
                  .filter(
                    (b) =>
                      b.bearing != null && b.bearing >= 0 && b.bearing <= 360,
                  ) ?? []
              }
              elTarget={50}
            ></RealtimePolarGraph>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const FiringAlertAnalyticsComponent = ({
  stationId,
  alertDetails,
  alertDataset,
}) => {
  const {selected_tenant_id}=useAuth();
  const [isConnected, subscribe, unsubscribe] = useWebSocket();
  const channelName = `${selected_tenant_id}-${stationId}-interferences`;

  const [newSamplePerformance, setNewSamplePerformance] = useState([]);
  const [newSampleDirection, setNewSampleDirection] = useState([]);

  useEffect(() => {
    if (isConnected === true) {
      // Subscribe to the desired WebSocket channel
      subscribe(channelName, handleGnssReceiveCallbackMessage);
    }
    // Cleanup subscription on component unmount
    return () => {
      unsubscribe(channelName);
    };
  }, [isConnected, subscribe, unsubscribe]);

  // const maxBandwidth = useMemo(() => {
  //   if (!alertDataset?.length) return 1;

  //   return alertDataset.reduce((max, d) => {
  //     const value = d.bandwidth / 1e6;
  //     return value > max ? value : max;
  //   }, 0);
  // }, [alertDataset]);

  // const [maxBandwidth, setMaxBandwidth] = useState(() => {
  //   if (!alertDataset?.length) return 1;

  //   return alertDataset.reduce((max, d) => {
  //     const value = d.bandwidth / 1e6;
  //     return value > max ? value : max;
  //   }, 0);
  // });

  const [maxBandwidth, setMaxBandwidth] = useState(0.02);
  const [nrSv, setNrSV] = useState(null);

  useEffect(() => {
    if (!newSamplePerformance?.length) return;

    const batchMax = newSamplePerformance.reduce((max, d) => {
      const value = d.interferenceBandwidth / 1e3;
      return value > max ? value : max;
    }, 0);

    setMaxBandwidth((prevMax) => (batchMax > prevMax ? batchMax : prevMax));
  }, [newSamplePerformance]);

  var handleGnssReceiveCallbackMessage = useCallback(
    (stompMessage) => {
      const payload = JSON.parse(stompMessage.body);

      const data = payload?.data;
      const details = data?.interferenceEvents;

      if (!details?.interferenceDetails?.length) return;

      const sample = details.interferenceDetails
        .filter((interference) => {
          return (
            interference.interferenceFrequency === alertDetails.centralFreq
          );
        })
        .map((item) => ({
          ...item,
          nrSV: data?.nrSV,
        }));

      if (sample) {
        setNewSamplePerformance(sample);
      } else setNewSamplePerformance([]);
    },
    [stationId],
  );

  return (
    <>
      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={newSamplePerformance.map((sample) => ({
                interferenceFrequency: sample?.interferenceFrequency / 1e6,
              }))}
              defaultOptiopns={{
                title: "Interference central frequency over time",
                xLabelName: "Time",
                yLabelName: "Frequency(MHz)",
                yMinValue: 1000,
                yMaxValue: 1800,
              }}
              lookback={160}
              initialDataset={alertDataset?.interferenceFrequency?.map(
                (sample) => ({
                  timestamp: sample?.timestamp,
                  interferenceFrequency: sample?.value / 10 ** 6,
                }),
              )}
              // freeze={alertDetails.state === AlertState.FIRING ? false : true}
            ></RealtimeDotsGraph>
          </Card>
        </Col>

        <Col className="col-md-12 col-lg-12 col-xl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={
                newSamplePerformance
                  ?.filter((sample) => sample?.bearing != null)
                  ?.map((sample) => ({
                    interferenceFrequency: sample?.bearing,
                  })) ?? []
              }
              defaultOptiopns={{
                title: "Bearing over time XY plot",
                xLabelName: "Time",
                yLabelName: "Bearing (degs)",
                yMinValue: 0,
                yMaxValue: 360,
                yTicks: 6,
              }}
              lookback={160}
              initialDataset={alertDataset?.bearing?.map((sample) => ({
                timestamp: sample.timestamp,
                interferenceFrequency: sample?.bearing,
              }))}
              freeze={alertDetails.state === AlertState.FIRING ? false : true}
            ></RealtimeDotsGraph>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0 d-flex flex-column">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeDotsGraph
              width={600}
              height={250}
              interferences={newSamplePerformance.map((sample) => ({
                interferenceFrequency: sample?.nrSV,
              }))}
              defaultOptiopns={{
                title: "Nr. of satellites",
                xLabelName: "Time",
                yLabelName: "Count",
                yMinValue: 0,
                yMaxValue: 50,
              }}
              lookback={160}
              initialDataset={alertDataset?.nrSV?.map((sample) => ({
                timestamp: sample?.timestamp,
                interferenceFrequency: sample?.value,
              }))}
              freeze={alertDetails.state === AlertState.FIRING ? false : true}
            ></RealtimeDotsGraph>
          </Card>
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <RealtimeAreaGraph
              width={600}
              height={250}
              interferences={newSamplePerformance
                ?.filter((sample) => sample?.interferenceBandwidth != null)
                ?.map((sample) => ({
                  interferenceFrequency:
                    sample?.interferenceBandwidth / 10 ** 3,
                }))}
              defaultOptiopns={{
                title: "Interference bandwidth over time",
                xLabelName: "Time",
                yLabelName: "Bandwidth(kHz)",
                yMinValue: 0,
                yTicks: 6,
              }}
              lookback={160}
              initialDataset={alertDataset?.bandwidth?.map((sample) => ({
                timestamp: sample.timestamp,
                interferenceFrequency: sample.value / 10 ** 3,
              }))}
              freeze={alertDetails.state === AlertState.FIRING ? false : true}
            ></RealtimeAreaGraph>
          </Card>
        </Col>
        <Col className="col-md-12 col-lg-12 col-xl-6 p-0 d-flex flex-column">
          <Card
            className="m-2 p-2 flex-fill justify-content-center align-items-center"
            style={{ background: "#141414" }}
          >
            <RealtimePolarGraph
              width={550}
              height={550}
              defaultOptiopns={{
                title: "Bearing realtime",
              }}
              azTarget={newSamplePerformance.map((sample) => ({
                bearing: sample?.bearing,
                centralFreq: formatFrequency(sample?.interferenceFrequency),
              }))}
              elTarget={50}
            ></RealtimePolarGraph>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const StoppedAlertAnalyticsComponent = ({ alertDetails, alertDataset }) => {
  const maxBandwidth = useMemo(() => {
    if (!alertDataset?.bandwidth?.length) return 1;

    return alertDataset?.bandwidth?.reduce((max, d) => {
      const value = d.value / 1e3;
      return value > max ? value : max;
    }, 0);
  }, [alertDataset]);

  return (
    <>
      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.interferenceFrequency?.map((sample) => ({
                timestamp: sample?.timestamp,
                value: sample?.value / 10 ** 6,
              }))}
              defaultOptiopns={{
                title: "Interference central frequency over time",
                xLabelName: "Time",
                yLabelName: "Frequency (MHz)",
                yMinValue: 1000,
                yMaxValue: 1800,
                yTicks: 6,
              }}
              intervalOptions={{
                startDate: alertDetails?.startTime,
                stopDate:
                  alertDetails?.startTime + alertDetails?.duration * 1000,
              }}
            ></DotsGraph>
          </Card>
        </Col>

        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.bearing
                ?.filter((sample) => sample?.value != null)
                .map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
              defaultOptiopns={{
                title: "Bearing over time XY plot",
                xLabelName: "Time",
                yLabelName: "Bearing (degs)",
                yMinValue: 0,
                yMaxValue: 360,
                yTicks: 6,
              }}
              intervalOptions={{
                startDate: alertDetails?.startTime,
                stopDate:
                  alertDetails?.startTime + alertDetails?.duration * 1000,
              }}
            ></DotsGraph>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0 d-flex flex-column">
          <Card className="m-2 p-2 flex-fill" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.nrSV
                ?.filter((sample) => sample?.value != null)
                ?.map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
              defaultOptiopns={{
                title: "Nr. of satellites over time ",
                xLabelName: "Time",
                yLabelName: "Satellites (count)",
                yMinValue: 0,
                yMaxValue: 50,
                yTicks: 6,
              }}
              intervalOptions={{
                startDate: alertDetails?.startTime,
                stopDate:
                  alertDetails?.startTime + alertDetails?.duration * 1000,
              }}
            ></DotsGraph>
          </Card>
          <Card className="m-2 p-2 flex-fill" style={{ background: "#141414" }}>
            <AreaGraph
              width={600}
              height={250}
              dataset={alertDataset?.bandwidth?.map((sample) => ({
                timestamp: sample?.timestamp,
                value: sample?.value / 10 ** 3,
              }))}
              defaultOptiopns={{
                title: "Interference bandwidth over time",
                xLabelName: "Time",
                yLabelName: "Bandwidth (kHz)",
                yMinValue: 0,
                yMaxValue: maxBandwidth,
                yTicks: 6,
              }}
              intervalOptions={{
                startDate: alertDetails?.startTime,
                stopDate:
                  alertDetails?.startTime + alertDetails?.duration * 1000,
              }}
            ></AreaGraph>
          </Card>
        </Col>
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0 d-flex flex-column">
          <Card
            className="m-2 p-2 flex-fill justify-content-center align-items-center"
            style={{ background: "#141414" }}
          >
            <PolarPlot
              width={550}
              height={550}
              defaultOptiopns={{
                title: "Bearing over time polar plot",
              }}
              dataset={alertDataset?.bearing
                ?.filter((sample) => sample?.value != null)
                .map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
            ></PolarPlot>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const CustomAnalyticsComponent = ({ alertDataset, onZoomGraph }) => {
  const maxBandwidth = useMemo(() => {
    if (!alertDataset?.bandwidth?.length) return 1;

    return alertDataset?.bandwidth?.reduce((max, d) => {
      const value = d.value / 1e3;
      return value > max ? value : max;
    }, 0);
  }, [alertDataset]);

  const AnalyticsOverview = () => {
    return (
      <>
        <Card
          className="m-0 p-0 border-danger"
          style={{ background: "transparent" }}
        >
          <Card.Body
            style={{ background: COLORS.ligthGrey }}
            className="rounded border-danger"
          >
            <Row className="p-0 m-0">
              <Col className="mt-0 pt-0">
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ExclamationOctagon className="me-1 text-danger"></ExclamationOctagon>
                  <span>
                    Critical interference events:{" "}
                    {alertDataset?.criticalInterferenceCount}
                  </span>
                </Card.Text>
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ExclamationDiamond className="me-1 text-warning"></ExclamationDiamond>
                  <span>
                    Warning interference events:{" "}
                    {alertDataset?.warningInterferenceCount}
                  </span>
                </Card.Text>
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ExclamationTriangle className="me-1 text-success"></ExclamationTriangle>
                  <span>
                    Informational interference events:{" "}
                    {alertDataset?.infoInterferenceCount}
                  </span>
                </Card.Text>
                <Card.Text className="small p-0 m-0 d-flex align-items-center">
                  <ExclamationSquare className="me-1 text-warning"></ExclamationSquare>
                  <span>Spoofing events: {alertDataset?.spoofingCount}</span>
                </Card.Text>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </>
    );
  };

  return (
    <Col>
      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-12 p-0">
          <AnalyticsOverview />
        </Col>
      </Row>

      <Row>
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.interferenceFrequency?.map((sample) => ({
                timestamp: sample?.timestamp,
                value: sample?.value / 10 ** 6,
              }))}
              defaultOptiopns={{
                title: "Interference central frequency over time",
                xLabelName: "Time",
                yLabelName: "Frequency (MHz)",
                yMinValue: 1000,
                yMaxValue: 1800,
                yTicks: 6,
                legend: [
                  { label: "Interferences", color: "red" },
                  { label: "Spoofing", color: "orange" },
                ],
              }}
              onRangeSelect={(e) => onZoomGraph(e)}
              areaOptions={alertDataset.spoofingIntervals}
              intervalOptions={{
                startDate: alertDataset?.interferenceFrequency?.[0]?.timestamp,
                stopDate:
                  alertDataset?.interferenceFrequency?.[
                    alertDataset?.interferenceFrequency?.length - 1
                  ]?.timestamp,
              }}
            ></DotsGraph>
          </Card>
        </Col>

        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0">
          <Card className="m-2 p-2" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.bearing
                ?.filter((sample) => sample?.value != null)
                .map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
              defaultOptiopns={{
                title: "Bearing over time XY plot",
                xLabelName: "Time",
                yLabelName: "Bearing (degs)",
                yMinValue: 0,
                yMaxValue: 360,
                yTicks: 6,
                legend: [
                  { label: "Interferences", color: "red" },
                  { label: "Spoofing", color: "orange" },
                ],
              }}
              onRangeSelect={(e) => onZoomGraph(e)}
              areaOptions={alertDataset.spoofingIntervals}
              intervalOptions={{
                startDate: alertDataset?.bearing?.[0]?.timestamp,
                stopDate:
                  alertDataset?.bearing?.[alertDataset?.bearing?.length - 1]
                    ?.timestamp,
              }}
            ></DotsGraph>
          </Card>
        </Col>
      </Row>

      <Row className="pt-0">
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0 d-flex flex-column">
          <Card className="m-2 p-2 flex-fill" style={{ background: "#141414" }}>
            <DotsGraph
              width={600}
              height={250}
              dataset={alertDataset?.nrSV
                ?.filter((sample) => sample?.value != null)
                ?.map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
              defaultOptiopns={{
                title: "Nr. of satellites over time ",
                xLabelName: "Time",
                yLabelName: "Satellites (count)",
                yMinValue: 0,
                yMaxValue: 50,
                yTicks: 6,
                legend: [
                  { label: "Interferences", color: "red" },
                  { label: "Spoofing", color: "orange" },
                ],
              }}
              onRangeSelect={(e) => onZoomGraph(e)}
              areaOptions={alertDataset.spoofingIntervals}
              intervalOptions={{
                startDate: alertDataset?.nrSV?.[0]?.timestamp,
                stopDate:
                  alertDataset?.nrSV?.[alertDataset?.nrSV?.length - 1]
                    ?.timestamp,
              }}
            ></DotsGraph>
          </Card>
          <Card className="m-2 p-2 flex-fill" style={{ background: "#141414" }}>
            <AreaGraph
              width={600}
              height={250}
              dataset={alertDataset?.bandwidth?.map((sample) => ({
                timestamp: sample?.timestamp,
                value: sample?.value / 10 ** 3,
              }))}
              defaultOptiopns={{
                title: "Interference bandwidth over time",
                xLabelName: "Time",
                yLabelName: "Bandwidth (kHz)",
                yMinValue: 0,
                yMaxValue: maxBandwidth,
                yTicks: 6,
              }}
              intervalOptions={{
                startDate: alertDataset?.nrSV?.[0]?.timestamp,
                stopDate:
                  alertDataset?.nrSV?.[alertDataset?.nrSV?.length - 1]
                    ?.timestamp,
              }}
            ></AreaGraph>
          </Card>
        </Col>
        <Col className="col-md-12 col-lg-12 col-xl-12 col-xxl-6 p-0 d-flex flex-column">
          <Card
            className="m-2 p-2 flex-fill justify-content-center align-items-center"
            style={{ background: "#141414" }}
          >
            <PolarPlot
              width={600}
              height={550}
              defaultOptiopns={{
                title: "Bearing over time polar plot",
              }}
              dataset={alertDataset?.bearing
                ?.filter((sample) => sample?.value != null)
                .map((sample) => ({
                  timestamp: sample?.timestamp,
                  value: sample?.value,
                }))}
            ></PolarPlot>
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

const SelectableRangeButtons = ({
  ranges,
  onChange,
  defaultSelected = null,
  allowDeselect = false,
  getNow = () => Date.now(),
  convert = (v) => v,
}) => {
  const [selected, setSelected] = useState(defaultSelected);

  const handleClick = (range) => {
    const isSame = selected === range.label;

    if (isSame && !allowDeselect) {
      return;
    }

    if (isSame && allowDeselect) {
      setSelected(null);
      onChange?.(null);
      return;
    }

    const end = getNow();
    const start = convert(end - range.value);
    const stop = convert(end);

    setSelected(range.label);

    onChange?.({
      label: range.label,
      start,
      stop,
    });
  };

  return (
    <Col className="d-flex flex-wrap gap-2">
      {ranges.map((r) => {
        const isActive = selected === r.label;

        return (
          <Button
            key={r.label}
            size="sm"
            variant={isActive ? "primary" : "outline-primary"}
            active={isActive}
            onClick={() => handleClick(r)}
          >
            {r.label}
          </Button>
        );
      })}
    </Col>
  );
};

export const InterferenceAnalysisCard = ({
  groupName,
  stationId,
  alertId,
  mode,
}) => {
  const api = useGnssApi();

  const minutes = (m) => m * 60 * 1000;
  const hours = (h) => h * 60 * 60 * 1000;
  const days = (d) => d * 24 * 60 * 60 * 1000;

  const [selectedRange, setSelectedRange] = useState();

  const ranges = useMemo(() => {
    const minutes = (m) => m * 60 * 1000;
    const hours = (h) => h * 60 * 60 * 1000;
    const days = (d) => d * 24 * 60 * 60 * 1000;

    return [
      { label: "Last 5 minutes", value: minutes(5) },
      { label: "Last 15 minutes", value: minutes(15) },
      { label: "Last 30 minutes", value: minutes(30) },
      { label: "Last 1 hour", value: hours(1) },
      { label: "Last 6 hours", value: hours(6) },
      { label: "Last 12 hours", value: hours(12) },
      { label: "Last 1 day", value: days(1) },
      { label: "Last 5 days", value: days(5) },
      { label: "Last 15 days", value: days(15) },
      { label: "Last 1 month", value: days(30) },
    ];
  }, []);

  const now = useCallback(() => Date.now(), []);

  const convertUtc = useCallback((date) => {
    const offsetMinutes = new Date(date).getTimezoneOffset();
    const pickedDate = new DateObject(date).toDate();

    const adjustedDate = new Date(
      pickedDate.getTime() - offsetMinutes * 60 * 1000,
    );

    return pickedDate.toISOString();
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [warning, setWarning] = useState({
    title: "",
    message: "",
    blocking: false,
  });

  const [analyticsStartDate, setAnalyticsStartDate] = useState(
    convertUtc(now() - ranges[0].value),
  );
  const [analyticsStopDate, setAnalyticsStopDate] = useState(convertUtc(now()));

  const [alertDetails, setAlertDetails] = useState({});
  const [alertDataset, setAlertDataset] = useState([]);
  const [alertZoomedDataset, setAlertZoomedDataset] = useState([]);
  const [isZoomedAnalytics, setIsZoomedAnalytics] = useState(false);

  useEffect(() => {
    const fetchAlert = async () => {
      try {
        setIsLoading(true);
        //First request
        let alertDetails = await api.getAlertDetailsByStationId(
          groupName,
          stationId,
          alertId,
        );

        if (
          (alertDetails?.status &&
            (alertDetails?.status !== "SUCCESS" ||
              alertDetails?.code !== 200)) ||
          !alertDetails
        ) {
          setIsLoading(false);
          setIsSuccess(false);
          setWarning({
            title: "Requested analytics for alert not found",
            message: `The analytics requested for alert with id ${alertId} are not available`,
            blocking: true,
          });

          return;
        }

        setAlertDetails(alertDetails?.data);

        // Second request (runs only after first finishes)
        let alertAnalytics = await api.getAlertAnalyticsByStationId(
          groupName,
          stationId,
          alertId,
        );

        if (
          (alertAnalytics?.status && alertAnalytics?.status !== "SUCCESS") ||
          !alertAnalytics ||
          alertAnalytics?.data.length === 0
        ) {
          setIsLoading(false);
          setIsSuccess(false);
          setWarning({
            title: "Analytics for request alert not found",
            message: `The analytics requested for alert with id ${alertId} are not available`,
            blocking: true,
          });

          return;
        }

        alertAnalytics && setAlertDataset(alertAnalytics?.data);
        setIsLoading(false);
        setIsSuccess(true);
      } catch (e) {
        setIsLoading(false);
        setIsSuccess(false);
        console.error("Failed to fetch alerts", e);
      }
    };

    alertId && alertId!=="realtime" && alertId!=="custom" ? fetchAlert() : setAlertDataset([]);
  }, [groupName, stationId, alertId]);

  const fetchAnalytics = useCallback(
    async (startDate, stopDate) => {
      try {
        setIsLoading(true);

        const alertAnalytics = await api.getAnalyticsByStationIdAndDateInterval(
          groupName,
          stationId,
          startDate,
          stopDate,
        );

        if (!alertAnalytics || alertAnalytics.status !== "SUCCESS") {
          setAlertDataset([]);
          return;
        }

        setAlertDataset(alertAnalytics.data || []);
      } finally {
        setIsLoading(false);
      }
    },
    [api, groupName, stationId],
  );

  const fetchZoomedAnalytics = useCallback(
    async (startDate, stopDate) => {
      try {
        setIsLoading(true);

        const alertAnalytics = await api.getAnalyticsByStationIdAndDateInterval(
          groupName,
          stationId,
          startDate,
          stopDate,
        );

        if (!alertAnalytics || alertAnalytics.status !== "SUCCESS") {
          setAlertZoomedDataset([]);
          return;
        }

        setAlertZoomedDataset(alertAnalytics.data || []);
      } finally {
        setIsLoading(false);
      }
    },
    [api, groupName, stationId],
  );

  const handleGetCustomAnalytics = () => {
    setIsZoomedAnalytics(false);
    setSelectedRange(null);
    fetchAnalytics(analyticsStartDate, analyticsStopDate);
  };

  const handleRangeChange = useCallback(
    (range) => {
      if (!range) return;

      setSelectedRange((prev) => {
        if (prev === range) return prev; // 🚀 prevents useless render
        return range;
      });

      setAnalyticsStartDate(range.start);
      setAnalyticsStopDate(range.stop);

      fetchAnalytics(range.start, range.stop);
    },
    [fetchAnalytics],
  );

  // -------------------------
  // JSX
  // -------------------------

  if (isLoading) return <LoadingSpinner />;

  return (
    <div>
      {isSuccess && !isLoading && alertId && alertId!=="realtime" && alertId!=="custom" && (
        <Card className="border-danger" style={{ background: "transparent" }}>
          <Card.Body
            style={{ background: COLORS.ligthGrey }}
            className="rounded border-danger"
          >
            <Col>
              <InterferenceAlertCard
                alertDetails={alertDetails}
                isSummary={true}
              />
              {alertDetails?.state === AlertState.FIRING && (
                <FiringAlertAnalyticsComponent
                  stationId={stationId}
                  alertDetails={alertDetails}
                  alertDataset={alertDataset}
                />
              )}

              {alertDetails?.state !== AlertState.FIRING && (
                <StoppedAlertAnalyticsComponent
                  alertDetails={alertDetails}
                  alertDataset={alertDataset}
                />
              )}
            </Col>
          </Card.Body>
        </Card>
      )}
      {alertId==="custom" && mode === "custom" && (
        <Card className="border-danger" style={{ background: "transparent" }}>
          <Card.Body
            style={{ background: COLORS.ligthGrey }}
            className="rounded border-danger"
          >
            <Row>
              <Col>Predefined interval</Col>
            </Row>
            <Row>
              <SelectableRangeButtons
                ranges={ranges}
                allowDeselect={false}
                defaultSelected={selectedRange?.label}
                convert={convertUtc}
                getNow={now}
                onChange={handleRangeChange}
              />
            </Row>

            <Row>
              <Col>
                <DateTimePickerEdit
                  label={"Start date (UTC)"}
                  startDate={
                    analyticsStartDate || convertUtc(now() - ranges[0].value)
                  }
                  disabled={false}
                  onChange={(date) => {
                    setAnalyticsStartDate(date);
                  }}
                />
              </Col>
              <Col>
                <DateTimePickerEdit
                  label={"Stop date (UTC)"}
                  startDate={analyticsStopDate || convertUtc(now())}
                  disabled={false}
                  onChange={(date) => {
                    setAnalyticsStopDate(date);
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Button size="sm" onClick={handleGetCustomAnalytics}>
                  Get interference analytics
                </Button>
                {isZoomedAnalytics && (
                  <Button
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setAnalyticsStartDate(selectedRange.start);
                      setAnalyticsStopDate(selectedRange.stop);
                      setIsZoomedAnalytics(false);
                    }}
                  >
                    Reset selection
                  </Button>
                )}
              </Col>
            </Row>
            <Row>
              <CustomAnalyticsComponent
                alertDataset={
                  isZoomedAnalytics ? alertZoomedDataset : alertDataset
                }
                onZoomGraph={(e) => {
                  setIsZoomedAnalytics(true);
                  setAnalyticsStartDate(e.startDate);
                  setAnalyticsStopDate(e.endDate);
                  fetchZoomedAnalytics(e.startDate, e.endDate);
                }}
              />
            </Row>
          </Card.Body>
        </Card>
      )}

      {alertId=="realtime" && mode === "realtime" && (
        <Card className="border-danger" style={{ background: "transparent" }}>
          <Card.Body
            style={{ background: COLORS.ligthGrey }}
            className="rounded border-danger"
          >
            <Row>
              <RealitimeAnalyticsComponent
                stationId={stationId}
                alertDetails={alertDetails}
                alertDataset={alertDataset}
              />
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};
